import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Tooltip } from '@amzn/storm-ui';
import { NavigationItem as NavigationItemType } from '../../config/navigationConfig';
import { SET_SELECTED_NAVIGATION_ID } from '../../store/actions/types';
import { useAppContext } from '../../store/appContext';
import { ART_EVENT_STATUS } from '../../../EditArtEvent/constants/artEventStatus';
import {
  NAVIGATION_IDS,
  FROZEN_DISABLED_NAVIGATION_IDS,
} from '../../constants/navigationIds';
import { canUserEdit } from '../../utils/helpers';
import {
  DropDownIcon,
  StyledIcon,
  StyledLink,
  StyledSideNavItem,
} from './LeftNavigationBar.styles';
import { SIDE_NAVIGATION_RESET_PARAM } from '../../../Shared/constants/commonConstants';
import { URLS } from '../../../Shared/constants/url';

interface NavigationItemProps {
  item: NavigationItemType;
  disabledByParent?: boolean;
}

export function NavigationItem({
  item,
  disabledByParent = false,
}: NavigationItemProps): React.ReactElement {
  const { t } = useTranslation();
  const { state, dispatch } = useAppContext();
  const location = useLocation();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const isActive = (itemId: string): boolean => {
    return state.selectedNavigationId === itemId;
  };

  const disabledByPermission = item.disableWithoutPermission
    && !canUserEdit(state.currentUser?.permissions);

  const isPrivileged = state.currentUser?.privilegedMode ?? false;
  const isFrozenNavItem =
    FROZEN_DISABLED_NAVIGATION_IDS.includes(item.id as any) &&
    state.selectedArtEventStatus === ART_EVENT_STATUS.FROZEN &&
    !isPrivileged;

  const disabled = disabledByPermission || disabledByParent || isFrozenNavItem;

  // This function helps to automatically expands parent navigation items when you're on a child route
  const hasActiveChild = (): boolean => {
    if (!item.children) return false;
    return item.children.some((child) =>
      location.pathname.startsWith(child.path)
    );
  };

  useEffect(() => {
    if (hasActiveChild()) {
      setIsExpanded(true);
    }
  }, [location.pathname]);

  const handleClick = (event?: React.MouseEvent) => {
    // Always allow expanding/collapsing children
    if (item.children) {
      setIsExpanded(!isExpanded);
      return;
    }

    if (disabled) {
      event?.preventDefault();
      return;
    }

   const params = new URLSearchParams(SIDE_NAVIGATION_RESET_PARAM);
    if (item.id === NAVIGATION_IDS.USERS) {
      event?.preventDefault();
      navigate(`${URLS.USERS}?${params}`);
    }
    if(item.id===NAVIGATION_IDS.BOOKINGS){
      event?.preventDefault();
      navigate(`${URLS.BOOKINGS}?${params}`);
    }
    if(item.id===NAVIGATION_IDS.EDIT_ART_EVENT){
      event?.preventDefault();
      navigate(`${URLS.ART_EVENT}?${params}`);
    }

    dispatch({
      type: SET_SELECTED_NAVIGATION_ID,
      payload: item.id,
    });
  };

  const navItemContent = (showChevron = false) => (
    <StyledSideNavItem
      isActive={isActive(item.id)}
      subItem={item.isSubItem}
    >
      <StyledIcon
        type={item.icon as any}
        size={item.iconSize || 'medium'}
      />{' '}
      {t(item.label)}
      {showChevron && (
        <DropDownIcon
          type={isExpanded ? 'chevron-up' : 'chevron-down'}
          size="small"
        />
      )}
    </StyledSideNavItem>
  );

  const wrapWithTooltip = (content: React.ReactNode) => (
    <Tooltip
      message={t('no-edit-permission-tooltip')}
      position="right"
      variant="default"
      trigger={content}
    />
  );

  const renderContent = () => {
    if (item.children) {
      return (
        <div id={`nav-item-${item.id}`} onClick={handleClick}>
          {navItemContent(true)}
        </div>
      );
    }

    const inner = (
      <StyledLink
        id={`nav-link-${item.id}`}
        to={item.path}
        onClick={handleClick}
        $disabled={disabled}
      >
        {navItemContent()}
      </StyledLink>
    );

    // Only show tooltip on leaf items disabled by their own permission, not inherited from parent
    return (disabledByPermission && !disabledByParent) ? wrapWithTooltip(inner) : inner;
  };

  return (
    <>
      {renderContent()}
      {item.children &&
        isExpanded &&
        item.children.map((child) => (
          <NavigationItem key={child.id} item={child} disabledByParent={disabledByPermission || disabledByParent} />
        ))}
    </>
  );
}
